import jwt_decode from 'jwt-decode';

export const UNAUTHORIZED_LEGACY_JOURNEY_MSG = 'You are not authorized';

export function redirectUnauthorizedLegacyJourney(router) {
  router.push({
    path: '/error-page',
    query: { msg: UNAUTHORIZED_LEGACY_JOURNEY_MSG, diy: 'true' },
  });
}

const DEFAULT_SESSION_START_ERROR = 'Failed to start session';

/** Prefer API errorDisplayMessage from generateTokenOverride failure bodies. */
export function resolveSessionExchangeErrorMessage(resp) {
  const errors = resp?.data?.errors ?? resp?.data?.data?.errors;
  const first = Array.isArray(errors) ? errors[0] : null;
  const msg = String(
    first?.errorDisplayMessage ?? first?.errorMessage ?? '',
  ).trim();
  return msg || DEFAULT_SESSION_START_ERROR;
}

export function redirectDiySessionFailure(router, message) {
  const msg = String(message ?? '').trim() || DEFAULT_SESSION_START_ERROR;
  router.push({
    path: '/error-page',
    query: { msg, diy: 'true' },
  });
}

/**
 * Motor often puts JSON.stringify(journeyId) in the route query.
 * Normalize to a plain journey id string.
 */
export function normalizeJourneyIdQuery(raw) {
  const s = String(raw ?? '').trim();
  if (!s) {
    return '';
  }
  try {
    const parsed = JSON.parse(s);
    if (typeof parsed === 'string' || typeof parsed === 'number') {
      return String(parsed).trim();
    }
  } catch {
    /* plain id */
  }
  return s;
}

/**
 * JourneyId-only deep links require an existing session access token.
 * Session minting from journeyId alone is no longer allowed.
 */
export function guardLegacyJourneyIdAccess(store) {
  return Boolean(store?.state?.token);
}

/** Supported / commonly mistyped journey deep-link query keys (login without token is rejected). */
export const DIY_JOURNEY_ID_QUERY_KEYS = ['journeyId', 'journeyID', 'journey_id'];

export function getDiyResumeTokenFromRoute(route) {
  return String(route?.query?.resumeToken ?? '').trim();
}

/** Prefer `journeyId`; accept legacy `journeyID` / `journey_id` aliases. */
export function getDiyJourneyIdFromRoute(route) {
  const query = route?.query;
  if (!query || typeof query !== 'object') {
    return '';
  }
  for (const key of DIY_JOURNEY_ID_QUERY_KEYS) {
    const value = normalizeJourneyIdQuery(query[key]);
    if (value) {
      return value;
    }
  }
  return '';
}

/** True when DIY deep-link keys are present (including empty values). */
export function hasDiyDeepLinkQuery(route) {
  const query = route?.query;
  if (!query || typeof query !== 'object') {
    return false;
  }
  if (Object.prototype.hasOwnProperty.call(query, 'resumeToken')) {
    return true;
  }
  return DIY_JOURNEY_ID_QUERY_KEYS.some((key) =>
    Object.prototype.hasOwnProperty.call(query, key),
  );
}

/**
 * Fail-closed DIY land: unauthenticated users without a valid resumeToken
 * (or empty/invalid deep-link params) go to the unauthorized page.
 * @returns {boolean} true if the caller may continue loading
 */
export function ensureDiyLandAccess(route, store, router) {
  const resumeToken = getDiyResumeTokenFromRoute(route);
  const journeyId = getDiyJourneyIdFromRoute(route);
  const hasToken = Boolean(store?.state?.token);

  if (resumeToken) {
    return true;
  }
  if (journeyId) {
    if (!hasToken) {
      redirectUnauthorizedLegacyJourney(router);
      return false;
    }
    return true;
  }
  // Empty/invalid deep-link keys, or DIY land with no session → unauthorized
  if (!hasToken) {
    redirectUnauthorizedLegacyJourney(router);
    return false;
  }
  return true;
}

/**
 * Bootstrap DIY/deep-link session from route query.
 * Only mints a session from resumeToken when no access token exists.
 * JourneyId without a session token is rejected (unauthorized).
 *
 * @returns {Promise<{ accessToken: string, journeyId: string } | null>}
 */
export async function establishDiySessionFromRoute(route, backendApi, store) {
  const resumeToken = getDiyResumeTokenFromRoute(route);
  const journeyIdFromQuery = getDiyJourneyIdFromRoute(route);

  if (resumeToken) {
    if (!store?.state?.token) {
      return establishSessionFromResumeToken(resumeToken, backendApi, store);
    }
    const existingJourneyId =
      readJourneyIdFromSession() || store.state.journeyId || '';
    return { accessToken: store.state.token, journeyId: existingJourneyId };
  }

  if (journeyIdFromQuery) {
    if (!store?.state?.token) {
      throw new Error(UNAUTHORIZED_LEGACY_JOURNEY_MSG);
    }
    persistJourneyId(journeyIdFromQuery, store);
    return { accessToken: store.state.token, journeyId: journeyIdFromQuery };
  }

  return null;
}

function decodeAccessTokenPayload(accessToken) {
  if (!accessToken || typeof accessToken !== 'string') {
    return null;
  }
  try {
    const decoded = jwt_decode(accessToken);
    const payload = decoded?.PAYLOAD;
    return payload && typeof payload === 'object' ? payload : null;
  } catch {
    return null;
  }
}

export function decodeJourneyIdFromAccessToken(accessToken) {
  const payload = decodeAccessTokenPayload(accessToken);
  return String(payload?.journey_id ?? '').trim();
}

export function resolveRmIdFromJourneyId(journeyId) {
  const resolved = normalizeJourneyIdQuery(journeyId);
  if (!resolved) {
    return '';
  }
  return resolved.split('/')[0];
}

/** Motor sessionStorage stores journey_id as JSON.stringify(value). */
export function persistJourneyId(journeyId, store) {
  const resolved = normalizeJourneyIdQuery(journeyId);
  if (!resolved) {
    return;
  }
  sessionStorage.setItem('journey_id', JSON.stringify(resolved));
  store.commit('setJourneyId', resolved);
}

export function readJourneyIdFromSession() {
  const raw = sessionStorage.getItem('journey_id');
  if (!raw) {
    return '';
  }
  return normalizeJourneyIdQuery(raw);
}

/** Coalesce concurrent generateTokenOverride calls with the same payload. */
const inflightExchanges = new Map();

function buildExchangeKey(payload) {
  if (payload?.resumeToken) {
    return `resumeToken:${payload.resumeToken}`;
  }
  if (payload?.journey_id) {
    return `journey_id:${payload.journey_id}`;
  }
  return '';
}

async function exchangeSessionToken(payload, backendApi, store) {
  if (store?.state?.token) {
    return store.state.token;
  }

  const key = buildExchangeKey(payload);
  if (key && inflightExchanges.has(key)) {
    return inflightExchanges.get(key);
  }

  const promise = (async () => {
    if (store?.state?.token) {
      return store.state.token;
    }
    const resp = await backendApi('generateTokenOverride', payload);
    const accessToken = resp?.data?.data?.token?.access_token;
    if (!accessToken) {
      throw new Error(resolveSessionExchangeErrorMessage(resp));
    }
    store.commit('setToken', accessToken);
    return accessToken;
  })().finally(() => {
    if (key) {
      inflightExchanges.delete(key);
    }
  });

  if (key) {
    inflightExchanges.set(key, promise);
  }
  return promise;
}

export async function establishSessionFromResumeToken(resumeToken, backendApi, store) {
  const resolved = String(resumeToken ?? '').trim();
  if (!resolved) {
    throw new Error('Resume token is required');
  }
  const accessToken = await exchangeSessionToken({ resumeToken: resolved }, backendApi, store);
  const journeyId = decodeJourneyIdFromAccessToken(accessToken);
  if (!journeyId) {
    throw new Error('Journey id is required');
  }
  persistJourneyId(journeyId, store);
  return { accessToken, journeyId };
}

export async function establishSessionFromJourneyIdLegacy(journeyId, backendApi, store) {
  const rmId = resolveRmIdFromJourneyId(journeyId);
  if (!rmId) {
    throw new Error('Journey id is required');
  }
  const accessToken = await exchangeSessionToken({ journey_id: rmId }, backendApi, store);
  const resolvedJourneyId = decodeJourneyIdFromAccessToken(accessToken);
  if (!resolvedJourneyId) {
    throw new Error('Journey id is required');
  }
  persistJourneyId(resolvedJourneyId, store);
  return { accessToken, journeyId: resolvedJourneyId };
}
